import React, { Component } from 'react';
import { error as logError } from '../../utils/logger';
import { isStrictMode } from '../../utils/strictMode';
export interface ErrorFallbackProps {
  error: Error;
  errorInfo: React.ErrorInfo | null;
  onReset: () => void;
}
interface BaseErrorBoundaryProps {
  children: React.ReactNode;
  /** Prefix for log messages (e.g. "Global", "Practice Session", "Question Renderer") */
  logPrefix: string;
  /** Extra context to include in log output */
  logContext?: Record<string, unknown>;
  /** When this key changes, the error state resets automatically */
  resetKey?: string | number;
  /** Called after error state is reset */
  onReset?: () => void;
  /** Render function for the fallback UI */
  fallback: (props: ErrorFallbackProps) => React.ReactNode;
}
interface BaseErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}
/**
 * BaseErrorBoundary — Reusable error boundary with shared lifecycle logic.
 *
 * Handles: constructor, getDerivedStateFromError, componentDidCatch,
 * isStrictMode bypass, resetKey-based auto-reset, and dev-mode logging.
 *
 * Each specific boundary (Global, Practice, Question) wraps this with
 * its own fallback UI via the `fallback` render prop.
 */
export class BaseErrorBoundary extends Component<BaseErrorBoundaryProps, BaseErrorBoundaryState> {
  constructor(props: BaseErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }
  static getDerivedStateFromError(error: Error): Partial<BaseErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    logError(`${this.props.logPrefix} Error:`, {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      ...this.props.logContext
    });
    this.setState({
      errorInfo
    });
  }
  componentDidUpdate(prevProps: BaseErrorBoundaryProps): void {
    if (this.props.resetKey !== undefined && prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null
      });
    }
  }
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    this.props.onReset?.();
  };
  render(): React.ReactNode {
    if (isStrictMode()) {
      return this.props.children;
    }
    if (this.state.hasError && this.state.error) {
      return this.props.fallback({
        error: this.state.error,
        errorInfo: this.state.errorInfo,
        onReset: this.handleReset
      });
    }
    return this.props.children;
  }
}